import {
    Box,
    Flex,
    Icon,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorMode,
    useColorModeValue,
} from "@chakra-ui/react";
import AdminStatsApi from "Api/AdminStatsApi";
import { useLoading } from "Providers/LoadingProvider";
import { useEffect, useState } from "react";
import { TbAntenna, TbBell, TbChartBar, TbCoin, TbMap, TbMapPin, TbUsers } from "react-icons/tb";
import ReactApexChart from "react-apexcharts";

function AdminStats() {
    const [stats, setStats] = useState(null);
    const loading = useLoading();
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const subtleBg = useColorModeValue("gray.50", "gray.700");
    const labelColor = isDark ? "#A0AEC0" : "#718096";
    const subtleTextColor = useColorModeValue("gray.600", "gray.400");

    const loadStats = async () => {
        loading.show();
        const response = await AdminStatsApi.get();
        setStats(response.json);
        loading.hide();
    };

    useEffect(() => {
        loadStats();
    }, []);

    if (!stats) return null;

    const signupsChartOptions = {
        chart: { type: "area", toolbar: { show: false }, sparkline: { enabled: false }, background: "transparent" },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
        colors: ["#2D6B3F"],
        xaxis: {
            categories: stats.signups_chart.map((d) => {
                const parts = d.date.split("-");
                return `${parts[2]}/${parts[1]}`;
            }),
            labels: { show: true, rotate: -45, style: { fontSize: "10px", colors: labelColor } },
            tickAmount: 10,
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: labelColor } } },
        dataLabels: { enabled: false },
        tooltip: { theme: isDark ? "dark" : "light", y: { formatter: (val) => `${val} cadastro(s)` } },
    };

    const signupsChartSeries = [
        { name: "Cadastros", data: stats.signups_chart.map((d) => d.count) },
    ];

    const revenueChartOptions = {
        chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
        colors: ["#3182CE"],
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        xaxis: {
            categories: stats.revenue_per_month.map((d) => {
                const parts = d.month.split("-");
                return `${parts[1]}/${parts[0]}`;
            }),
            labels: { style: { colors: labelColor } },
        },
        yaxis: {
            labels: { formatter: (val) => `R$ ${val.toFixed(0)}`, style: { colors: labelColor } },
        },
        dataLabels: { enabled: false },
        tooltip: {
            theme: isDark ? "dark" : "light",
            y: { formatter: (val) => `R$ ${val.toFixed(2)}` },
        },
    };

    const revenueChartSeries = [
        { name: "Receita", data: stats.revenue_per_month.map((d) => parseFloat(d.total)) },
    ];

    return (
        <Flex direction="column" gap={6} pb={8}>
            {/* Header */}
            <Box bg={cardBg} borderRadius="xl" overflow="hidden" boxShadow="md" border="1px solid" borderColor={borderColor}>
                <Box bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)" py={5} px={6}>
                    <Flex align="center" gap={3}>
                        <Flex align="center" justify="center" bg="whiteAlpha.200" borderRadius="lg" p={2}>
                            <Icon as={TbChartBar} color="white" boxSize={5} />
                        </Flex>
                        <Box>
                            <Text color="white" fontWeight="bold" fontSize="xl">Estatísticas</Text>
                            <Text color="whiteAlpha.800" fontSize="sm">Painel Administrativo</Text>
                        </Box>
                    </Flex>
                </Box>
            </Box>

            {/* User Stats Cards */}
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} gap={4}>
                <StatCard
                    label="Total de Usuários"
                    value={stats.users.total}
                    bg={cardBg}
                    borderColor={borderColor}
                    subtleTextColor={subtleTextColor}
                />
                <StatCard
                    label="Ativos"
                    value={stats.users.active}
                    color="green.500"
                    bg={cardBg}
                    borderColor={borderColor}
                    subtleTextColor={subtleTextColor}
                />
                <StatCard
                    label="Suspensos"
                    value={stats.users.suspended}
                    color="red.500"
                    bg={cardBg}
                    borderColor={borderColor}
                    subtleTextColor={subtleTextColor}
                />
                <StatCard
                    label="Não Renovaram"
                    value={stats.users.churned}
                    color="orange.500"
                    bg={cardBg}
                    borderColor={borderColor}
                    subtleTextColor={subtleTextColor}
                />
                <StatCard
                    label="Expirando em 30d"
                    value={stats.users.expiring_soon}
                    color="yellow.600"
                    bg={cardBg}
                    borderColor={borderColor}
                    subtleTextColor={subtleTextColor}
                />
            </SimpleGrid>

            {/* Signups Chart */}
            <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                <Text fontWeight="bold" fontSize="lg" mb={4}>
                    <Icon as={TbUsers} mr={2} />
                    Cadastros nos Últimos 30 Dias
                </Text>
                <ReactApexChart
                    options={signupsChartOptions}
                    series={signupsChartSeries}
                    type="area"
                    height={280}
                />
            </Box>

            {/* Stations & Alerts */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
                <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                    <Text fontWeight="bold" fontSize="lg" mb={4}>
                        <Icon as={TbAntenna} mr={2} />
                        Estações
                    </Text>
                    <SimpleGrid columns={2} gap={4} mb={4}>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Total</Text>
                            <Text fontSize="xl" fontWeight="bold">{stats.stations.total}</Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Offline</Text>
                            <Text fontSize="xl" fontWeight="bold" color="red.500">{stats.stations.offline}</Text>
                        </Box>
                    </SimpleGrid>
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>Provedor</Th>
                                <Th isNumeric>Estações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {stats.stations.by_provider.map((p, i) => (
                                <Tr key={i}>
                                    <Td>{p.provider}</Td>
                                    <Td isNumeric fontWeight="semibold">{p.count}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>

                <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                    <Text fontWeight="bold" fontSize="lg" mb={4}>
                        <Icon as={TbBell} mr={2} />
                        Alertas
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} mb={4}>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Usuários com Alertas</Text>
                            <Text fontSize="xl" fontWeight="bold">{stats.alerts.users_with_alerts}</Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Total de Alertas</Text>
                            <Text fontSize="xl" fontWeight="bold">{stats.alerts.total}</Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Notificações Enviadas</Text>
                            <Text fontSize="xl" fontWeight="bold">{stats.alerts.notifications_sent}</Text>
                        </Box>
                    </SimpleGrid>
                    {stats.alerts.by_type.length > 0 && (
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Tipo</Th>
                                    <Th isNumeric>Alertas</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {stats.alerts.by_type.map((t, i) => (
                                    <Tr key={i}>
                                        <Td textTransform="capitalize">{t.type}</Td>
                                        <Td isNumeric fontWeight="semibold">{t.count}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </Box>
            </SimpleGrid>

            {/* Revenue Section */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
                {/* Revenue Stats */}
                <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                    <Text fontWeight="bold" fontSize="lg" mb={4}>
                        <Icon as={TbCoin} mr={2} />
                        Receita
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Receita Total</Text>
                            <Text fontSize="xl" fontWeight="bold" color="green.500">
                                R$ {stats.revenue.total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Este Mês</Text>
                            <Text fontSize="xl" fontWeight="bold">
                                R$ {stats.revenue.this_month.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Últimos 30 Dias</Text>
                            <Text fontSize="xl" fontWeight="bold">
                                R$ {stats.revenue.last_30_days.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Total de Pagamentos</Text>
                            <Text fontSize="xl" fontWeight="bold">{stats.revenue.transactions_count}</Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Usuários Pagantes</Text>
                            <Text fontSize="xl" fontWeight="bold">{stats.revenue.paying_users}</Text>
                        </Box>
                        <Box bg={subtleBg} p={4} borderRadius="lg">
                            <Text fontSize="sm" color={subtleTextColor}>Receita Média por Usuário</Text>
                            <Text fontSize="xl" fontWeight="bold">
                                R$ {stats.revenue.avg_per_user.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </Text>
                        </Box>
                    </SimpleGrid>
                </Box>

                {/* Revenue Chart */}
                <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                    <Text fontWeight="bold" fontSize="lg" mb={4}>
                        Receita Mensal (12 meses)
                    </Text>
                    {stats.revenue_per_month.length > 0 ? (
                        <ReactApexChart
                            options={revenueChartOptions}
                            series={revenueChartSeries}
                            type="bar"
                            height={250}
                        />
                    ) : (
                        <Flex align="center" justify="center" h="250px">
                            <Text color="gray.500">Nenhuma transação encontrada</Text>
                        </Flex>
                    )}
                </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
                {/* Top Cities */}
                <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                    <Text fontWeight="bold" fontSize="lg" mb={4}>
                        <Icon as={TbMapPin} mr={2} />
                        Top Cidades
                    </Text>
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>Cidade</Th>
                                <Th>Estado</Th>
                                <Th isNumeric>Usuários</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {stats.top_cities.map((city, i) => (
                                <Tr key={i}>
                                    <Td>{i + 1}</Td>
                                    <Td>{city.city || "-"}</Td>
                                    <Td>{city.state || "-"}</Td>
                                    <Td isNumeric fontWeight="semibold">{city.count}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>

                {/* Per State */}
                <Box bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor={borderColor} p={6}>
                    <Text fontWeight="bold" fontSize="lg" mb={4}>
                        <Icon as={TbMap} mr={2} />
                        Usuários e Estações por Estado
                    </Text>
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>Estado</Th>
                                <Th isNumeric>Usuários</Th>
                                <Th isNumeric>Estações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {stats.per_state?.map((row, i) => (
                                <Tr key={i}>
                                    <Td>{row.state || "-"}</Td>
                                    <Td isNumeric fontWeight="semibold">{row.users_count}</Td>
                                    <Td isNumeric fontWeight="semibold">{row.stations_count}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </SimpleGrid>
        </Flex>
    );
}

function StatCard({ label, value, color, bg, borderColor, subtleTextColor }) {
    return (
        <Box bg={bg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor={borderColor} p={4}>
            <Stat>
                <StatLabel fontSize="xs" color={subtleTextColor || "gray.500"}>{label}</StatLabel>
                <StatNumber fontSize="2xl" color={color}>{value}</StatNumber>
            </Stat>
        </Box>
    );
}

export default AdminStats;
